import { HttpClient, HttpHeaders } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import { catchError, finalize, map } from "rxjs/operators";
import { AbstractService } from "../../abstractions/abstract.service";
import { Globals } from "../../app.globals";
import { AllAttachmentsDto } from "../../services/settings/subContractor.service";
import {
    CreateUrlRequest,
    FilesAndFolders,
    FileVersion,
    MoveRequest,
    RenameFileByUrlRequest,
    RenameFileRequest,
} from "./dms.models";

@Injectable()
export class DmsService extends AbstractService {
    projectId: number = 0;
    path: string = "";

    constructor(public http: HttpClient, public globals: Globals) {
        super(http, globals);
    }

    getDocuments(folder: string = null): Observable<File[]> {
        var query = this.encodeUriQuery(folder || this.path);
        this.showSpinner();
        return this.http
            .get(this.getApiUrl() + `/getall/?folder=${query}`)
            .pipe(
                map((res) => res as File[]),
                catchError(this.handleError),
                finalize(() => this.hideSpinner())
            );
    }

    getDocumentsBySecurityCode(
        securityCode: string,
        folder: string = null
    ): Observable<File[]> {
        var query = this.encodeUriQuery(folder || this.path);
        this.showSpinner();
        return this.http
            .get(
                this.getApiUrl() +
                    `/bysecuritycode/${securityCode}?folder=${query}`
            )
            .pipe(
                map((res) => res as File[]),
                catchError(this.handleError),
                finalize(() => this.hideSpinner())
            );
    }

    getApiUrl(): string {
        return `api/projects/${this.projectId}/dms`;
    }

    getUploadUrl(path: string = null): string {
        path = path || this.path;
        return this.getApiUrl() + "?folder=" + this.encodeUriQuery(path);
    }

    uploadFileInFolder(
        path: string = null,
        formData: FormData,
        headers: HttpHeaders | { [header: string]: string | string[] } = null
    ): Observable<any> {
        path = path || this.path;

        return this.http.post(path, formData, { headers: headers }).pipe(
            map((res) => res),
            catchError(this.handleError)
        );
    }

    uploadFileInFolderDropped(
        folderPath: string = null,
        formData: FormData,
        headers: HttpHeaders | { [header: string]: string | string[] } = null
    ): Observable<any> {
        return this.http.post(folderPath, formData, { headers: headers }).pipe(
            map((res) => res),
            catchError(this.handleError),
            finalize(() => this.hideSpinner())
        );
    }

    createDirectory(path, showSpinner: boolean = true): Observable<string> {
        if (showSpinner) this.showSpinner();
        return this.http
            .post(
                this.getApiUrl() + "/createDirectory",
                { path: path },
                { responseType: "text" }
            )
            .pipe(
                map((res) => res),
                catchError(this.handleError),
                finalize(() => {
                    if (showSpinner) this.hideSpinner();
                })
            );
    }

    renameFolder(request: MoveRequest): Observable<any> {
        this.showSpinner();
        return this.http.put(this.getApiUrl() + "/renameFolder", request).pipe(
            catchError(this.handleError),
            finalize(() => this.hideSpinner())
        );
    }

    renameFile(request: RenameFileRequest): Observable<any> {
        this.showSpinner();
        return this.http.put(this.getApiUrl() + "/renameFile", request).pipe(
            catchError(this.handleError),
            finalize(() => this.hideSpinner())
        );
    }

    renameFileByUrl(request: RenameFileByUrlRequest): Observable<any> {
        this.showSpinner();
        return this.http
            .put(this.getApiUrl() + "/renameFileByUrl", request)
            .pipe(
                catchError(this.handleError),
                finalize(() => this.hideSpinner())
            );
    }

    getFiles(folder: string = null): Observable<File[]> {
        var query = this.encodeUriQuery(folder || this.path);
        this.showSpinner();
        return this.http
            .get(this.getApiUrl() + `/getFiles?folder=${query}`)
            .pipe(
                map((res) => res as File[]),
                catchError(this.handleError),
                finalize(() => this.hideSpinner())
            );
    }

    getFilesAndSubFolders(folder: string = null): Observable<FilesAndFolders> {
        var query = this.encodeUriQuery(folder || this.path);
        return this.http
            .get(this.getApiUrl() + `/getFilesAndSubFolders?folder=${query}`)
            .pipe(
                map((res) => res as FilesAndFolders),
                catchError(this.handleError)
            );
    }

    deleteFolder(url: string): Observable<null> {
        this.showSpinner();
        return this.http
            .request("delete", this.getApiUrl() + "/folder", {
                body: { url: url },
            })
            .pipe(
                catchError(this.handleError),
                finalize(() => this.hideSpinner())
            );
    }

    deleteFile(url: string): Observable<null> {
        this.showSpinner();
        return this.http
            .request("delete", this.getApiUrl() + "/file", {
                body: { url: url },
            })
            .pipe(
                catchError(this.handleError),
                finalize(() => this.hideSpinner())
            );
    }

    deleteFileSec(url: string, securityToken: string): Observable<null> {
        this.showSpinner();
        return this.http
            .request(
                "delete",
                this.getApiUrl() + "/fileSec?securityToken=" + securityToken,
                {
                    body: {
                        url: url,
                    },
                }
            )
            .pipe(
                catchError(this.handleError),
                finalize(() => this.hideSpinner())
            );
    }

    search(folder: string, filter: string): Observable<FilesAndFolders> {
        var query = this.encodeUriQuery(folder);
        return this.http
            .get(this.getApiUrl() + `/search/${filter}?folder=${query}`)
            .pipe(
                map((res) => res as FilesAndFolders),
                catchError(this.handleError)
            );
    }

    getAggregateId(): Observable<string> {
        this.showSpinner();
        return this.http
            .get(this.getApiUrl() + "/getAggregateId", { responseType: "text" })
            .pipe(
                map((res) => res),
                catchError(this.handleError),
                finalize(() => this.hideSpinner())
            );
    }

    createUrl(request: CreateUrlRequest) {
        this.showSpinner();
        return this.http
            .post(this.getApiUrl() + "/createUrl", request, {
                responseType: "text",
            })
            .pipe(
                map((res) => res),
                catchError(this.handleError),
                finalize(() => this.hideSpinner())
            );
    }

    getSharedFolderLink(folder: string = null): Observable<string> {
        var query = this.encodeUriQuery(folder);
        this.showSpinner();
        return this.http
            .get(this.getApiUrl() + `/getSharedFolderLink/?folder=${query}`, {
                responseType: "text",
            })
            .pipe(
                map((res) => res),
                catchError(this.handleError),
                finalize(() => this.hideSpinner())
            );
    }

    getSharedPath(securityToken: string) {
        this.showSpinner();
        return this.http
            .get(this.getApiUrl() + `/getSharedFolderPath/${securityToken}`, {
                responseType: "text",
            })
            .pipe(
                map((res) => res),
                catchError(this.handleError),
                finalize(() => this.hideSpinner())
            );
    }

    downloadFolder(request: CreateUrlRequest) {
        this.showSpinner();
        return this.http
            .post(this.getApiUrl() + "/downloadFolder", request, {
                observe: "response",
                responseType: "blob",
            })
            .pipe(
                map((res) =>
                    this.downloadFile(res.body, res.body.type, request.filename)
                ),
                catchError(this.handleError),
                finalize(() => this.hideSpinner())
            );
    }

    downloadFolderByFullUrl(request: CreateUrlRequest) {
        this.showSpinner();
        return this.http
            .post(this.getApiUrl() + "/downloadFolder/fullurl", request, {
                observe: "response",
                responseType: "blob",
            })
            .pipe(
                map((res) =>
                    this.downloadFile(res.body, res.body.type, request.filename)
                ),
                catchError(this.handleError),
                finalize(() => this.hideSpinner())
            );
    }

    downloadAllAttachmentFiles(path: string, data: AllAttachmentsDto) {
        this.showSpinner();
        return this.http
            .post(path, data, { observe: "response", responseType: "blob" })
            .pipe(
                map((res) =>
                    this.downloadFile(
                        res.body,
                        res.body.type,
                        data.type === "Drawing"
                            ? "AllDrawings.zip"
                            : data.type === "Image"
                            ? "AllImages.zip"
                            : "AllDocuments.zip"
                    )
                ),
                catchError(this.handleError),
                finalize(() => this.hideSpinner())
            );
    }

    downloadAllResponseFiles(path: string, data: AllAttachmentsDto) {
        this.showSpinner();
        return this.http
            .post(path, data, { observe: "response", responseType: "blob" })
            .pipe(
                map((res) =>
                    this.downloadFile(
                        res.body,
                        res.body.type,
                        "ResponseFiles.zip"
                    )
                ),
                catchError(this.handleError),
                finalize(() => this.hideSpinner())
            );
    }

    downloadFile(blob: any, type: string, filename: string): string {
        const url = window.URL.createObjectURL(blob);

        const a = document.createElement("a");
        a.setAttribute("style", "display:none;");
        document.body.appendChild(a);

        a.href = url;
        a.download = filename;
        a.click();
        a.remove();
        return url;
    }

    downloadFileByUrl(url: string, filename: string): void {
        const a = document.createElement("a");
        a.setAttribute("style", "display:none;");
        document.body.appendChild(a);

        a.href = url;
        a.download = filename;
        a.target = "_blank";
        a.click();
        a.remove();
    }

    getVersions(url: string): Observable<FileVersion[]> {
        var query = this.encodeUriQuery(url);
        this.showSpinner();
        return this.http.get(this.getApiUrl() + `/versions?path=${query}`).pipe(
            map((res) => res as FileVersion[]),
            catchError(this.handleError),
            finalize(() => this.hideSpinner())
        );
    }

    encodeUriQuery(querySegment: string): string {
        return encodeURIComponent(decodeURIComponent(querySegment));
    }

    updateLabel(url: string, description: string): Promise<void> {
        this.showSpinner();

        if (url.indexOf("?") > -1) url = url.substr(0, url.indexOf("?"));

        return this.http
            .post(this.getApiUrl() + "/update-label", {
                url: url,
                label: description,
            })
            .pipe(
                map((res) => res),
                catchError(this.handleError),
                finalize(() => this.hideSpinner())
            )
            .toPromise();
    }

    updateFile(
        url: string,
        blob: Blob,
        fileName: string
    ): Promise<UpdateFileResponse> {
        try {
            if (url.indexOf("?") > -1) url = url.substring(0, url.indexOf("?"));

            this.showSpinner();

            let formData = new FormData();

            formData.append("file", blob, fileName);
            formData.append("originalUrl", url);

            return this.http
                .put(this.getApiUrl() + "/replace", formData)
                .pipe(
                    map((res) => res as UpdateFileResponse),
                    catchError(this.handleError),
                    finalize(() => this.hideSpinner())
                )
                .toPromise();
        } finally {
            this.hideSpinner();
        }
    }
}

class UpdateFileResponse {
    url: string;
}
